<script setup>
import ImportTrackedKeywordsAction from '@frontJs/components/actions/projectManagement/seo/ImportTrackedKeywordsAction.vue';
import UpdateTrackedKeywordAction from '@frontJs/components/actions/projectManagement/seo/UpdateTrackedKeywordAction.vue';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import UploadSimpleIcon from '@iconify-vue/ph/upload-simple';
import DisplayLineChartAction from '@js/components/actions/DisplayLineChartAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ExportButton from '@js/components/buttons/ExportButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import PillsCell from '@js/components/tables/cells/PillsCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { useRoute } from '@js/utils/route';
import { toastError } from '@js/utils/toast';
import { createColumnHelper } from '@tanstack/vue-table';
import { h, ref } from 'vue';

const { tenant, competitor, type, countries } = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    competitor: {
        type: Object,
        required: true,
    },
    type: {
        type: String,
        required: true,
    },
    countries: {
        type: Array,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    selectionIds,
    exportData,
    updateData,
    selectData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'keyword',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Mot-clé',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.keyword,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'intents',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Intentions',
                }),
            cell: (props) =>
                h(PillsCell, {
                    pillValues: props.row.original.formattedIntents.map(
                        (intent) => ({
                            value: intent.abbreviation,
                            title: intent.label,
                            type: 'primary',
                        }),
                    ),
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'seo_tracked_keywords.id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Suivi',
                }),
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.seoTrackedKeyword,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'url',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'URL',
                }),
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.url,
                    href: props.row.original.url,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'position',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Position',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.position,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'seo_indexed_urls.url',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Votre URL',
                }),
            cell: (props) =>
                props.row.original.yourUrl
                    ? h(LinkCell, {
                          text: props.row.original.yourUrl,
                          href: props.row.original.yourUrl,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'seo_keyword_position_history.position',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Votre position',
                    align: 'right',
                }),
            cell: (props) =>
                props.row.original.yourPosition
                    ? h(
                          NumberCell,
                          {
                              number: props.row.original.yourPosition,
                              color:
                                  props.row.original.yourPosition <
                                  props.row.original.position
                                      ? 'green'
                                      : props.row.original.yourPosition >
                                          props.row.original.position
                                        ? 'red'
                                        : null,
                          },
                          props.row.original.yourPositionChartData
                              ? {
                                    actions: () =>
                                        h(DisplayLineChartAction, {
                                            labels: props.row.original
                                                .yourPositionChartData.labels,
                                            datasets: [
                                                {
                                                    group: 'Position',
                                                    label: 'Position',
                                                    backgroundColor: '#4d65b9',
                                                    borderColor: '#4d65b9',
                                                    pointRadius: 0,
                                                    pointHoverRadius: 0,
                                                    fill: {
                                                        target: 'start',
                                                        above: '#e4e8f4',
                                                    },
                                                    tension: 0.4,
                                                    data: props.row.original
                                                        .yourPositionChartData
                                                        .data,
                                                    labels: props.row.original
                                                        .yourPositionChartData
                                                        .labels,
                                                    yAxisID: 'y',
                                                },
                                            ],
                                            title: 'Positions',
                                            withDecimalsAxis1: false,
                                            reverseAxis1: true,
                                            minAxis1: 1,
                                            maxAxis1: 100,
                                        }),
                                }
                              : {},
                      )
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'searchVolume',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Volume de recherche',
                }),
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.searchVolume,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'competitor_targets.website_variant_id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Site',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue:
                        props.row.original.competitorTarget.websiteVariant.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'competitor_targets.google_website_id',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Site Google',
                }),
            cell: (props) =>
                h(PillCell, {
                    pillValue:
                        props.row.original.competitorTarget.googleWebsite.name,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'fetchedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Dernière MAJ',
                }),
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.fetchedAt,
                }),
            enableSorting: false,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            props.row.original.seoTrackedKeyword === null
                                ? h(
                                      ImportTrackedKeywordsAction,
                                      {
                                          tenant,
                                          callback: updateData,
                                          keywords: props.row.original.keyword,
                                          disabledKeywords: true,
                                      },
                                      {
                                          button: ({ setIsOpen }) =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Importer',
                                                  callback: () =>
                                                      setIsOpen(true),
                                                  icon: UploadSimpleIcon,
                                              }),
                                      },
                                  )
                                : null,
                            props.row.original.seoTrackedKeyword !== null
                                ? h(
                                      UpdateTrackedKeywordAction,
                                      {
                                          tenant,
                                          countries,
                                          seoTrackedKeyword:
                                              props.row.original
                                                  .seoTrackedKeyword,
                                          callback: updateData,
                                      },
                                      {
                                          trigger: () =>
                                              h(ActionButton, {
                                                  icon: PencilSimpleIcon,
                                                  size: 'small',
                                                  square: true,
                                                  title: 'Modifier',
                                                  type: 'primary',
                                              }),
                                      },
                                  )
                                : null,
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.modules.postCompetitorHistories', {
        tenant: tenant.id,
        id: competitor.id,
    }),
    exportUrl: route(
        'front.projectManagement.modules.postExportCompetitorHistories',
        {
            tenant: tenant.id,
            id: competitor.id,
        },
    ),
    data: {
        competitorId: competitor.id,
        type,
    },
    selectionIdKey: 'id',
    columnPinning: {
        right: ['actions'],
    },
    columnVisibility: {
        intents: type === 'seo',
    },
});

const importLoading = ref(false);

const keywords = ref('');

function importAction(setIsOpen, setKeywords) {
    importLoading.value = true;

    selectData()
        .then((data) => {
            setKeywords('');
            keywords.value = '';

            data.forEach((item) => {
                if (item.seoTrackedKeyword === null) {
                    keywords.value += item.keyword + '\n';
                }
            });

            if (keywords.value === '') {
                toastError('Aucun mot-clé à importer.');
            } else {
                setKeywords(keywords.value);
                setIsOpen(true);
            }
        })
        .finally(() => {
            importLoading.value = false;
        });
}
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ExportButton
                :csv-export-callback="() => exportData('csv')"
                :xlsx-export-callback="() => exportData('xlsx')"
                :disabled="selectionIds.length === 0"
            />

            <ImportTrackedKeywordsAction
                :tenant="tenant"
                :callback="updateData"
                :keywords="keywords"
                disabled-keywords
            >
                <template #button="{ setIsOpen, setKeywords }">
                    <ActionButton
                        size="small"
                        title="Importer"
                        :callback="() => importAction(setIsOpen, setKeywords)"
                        :left-icon="UploadSimpleIcon"
                        :disabled="selectionIds.length === 0 || importLoading"
                        :loading="importLoading"
                    >
                        Importer
                    </ActionButton>
                </template>
            </ImportTrackedKeywordsAction>
        </template>
    </DataTableLayout>
</template>
